import { Children, cloneElement, Component } from "react";
import styled from "styled-components";

// We need this file imported here to ensure the cart service is initialized, otherwise things break
import cartService from "../services/cart"; // eslint-disable-line no-unused-vars

// Utils
import { currentPageData } from "../utils/currentPageData";
import { getPlanIdAndQuantityArray } from "../utils/bundle";

// Add global styles
import "../styles/index.scss";

// Shared Components across site
import FlyoutCart from "../components/cart/FlyoutCart";
import LocaleSuggester from "../components/global/LocaleSuggester";

// Store
import { connect } from "react-redux";
import { getStore } from "../store/createStore";

// Fermat utilities
import {
  getFermatCartProducts,
  getFermatDiscountCodes,
  getFermatAttribution,
} from "../utils/fermatBundle";

// Actions
import { appLoaded } from "../store/app/actions";
import {
  clearCartAndAddProducts,
  addProductsToCart,
} from "../store/cart/actions";

// Selectors
import cartProductSelectors from "../store/cart-product/selectors";
import cartSelectors from "../store/cart/selectors";
import {
  CustomerScope,
  QuickManageScope,
  hasScope,
  isAuthenticated,
  logout,
} from "../utils/authentication";
import CartController from "../components/CartController";
import SimpleHeader from "../components/global/SimpleHeader";
import SimpleFooter from "../components/global/navigation/SimpleFooter";

const SiteRoot = styled.div`
  padding-top: ${(p) => {
    // Account for offset and 4px loading bar at top of page
    return p.topOffset ? `${p.topOffset - 4}px` : "0";
  }} !important;
  // contain: paint; // this causes issue with flyout

  &:focus {
    outline: none;
  }
`;

export class RitualBase extends Component {
  constructor(props) {
    super(props);

    if (
      isAuthenticated() &&
      !hasScope(CustomerScope) &&
      !hasScope(QuickManageScope)
    ) {
      logout(false);
    }

    this.updatePageData = this.updatePageData.bind(this);

    this._bufferedOffset = null;
    this._isMounted = false;

    this.state = {
      productsToAdd: [],
      fermatData: null,
    };
  }

  // Triggers on first load
  componentDidMount() {
    this._isMounted = true;
    if (typeof window !== "undefined" && "performance" in window) {
      performance.mark("application-loaded");
    }

    getStore().dispatch(appLoaded());

    this.loadCheckHeadings();

    // Process Fermat cookie data
    const fermatCartProducts = getFermatCartProducts();
    const fermatDiscountCodes = getFermatDiscountCodes();
    const fermatAttribution = getFermatAttribution();

    if (fermatCartProducts && fermatCartProducts.length > 0) {
      // We have Fermat data - set Fermat data and products
      this.setState({
        fermatData: {
          products: fermatCartProducts,
          discountCodes: fermatDiscountCodes,
          attribution: fermatAttribution,
        },
        productsToAdd: fermatCartProducts,
      });

      // Set flag that cart processing is happening (Fermat)
      // This prevents checkout page from redirecting while cart is being built
      sessionStorage.setItem("cart_processing", "true");
      return; // Exit early - don't process query params
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);

    const cart = urlParams.get("cart");
    if (cart === "open") {
      cartService.openCart();
    }

    const addProductsFromQueryParam = (paramName, validIds, formatter) => {
      const param = urlParams.get(paramName);
      return param
        ? param
          .split(",")
          .filter((id) => validIds.includes(id))
          .map(formatter)
        : [];
    };

    const { validPlanIds, validProductOfferIds } = this.props;

    // Process query parameters for products to add
    const queryParamProducts = [
      ...addProductsFromQueryParam("pid", validPlanIds, (id) =>
        getPlanIdAndQuantityArray([id]),
      ).flat(),
      ...addProductsFromQueryParam("poid", validProductOfferIds, (id) => ({
        quantity: 1,
        productOfferId: id,
      })),
    ];

    // Process query parameters only if no Fermat data was found
    if (queryParamProducts.length > 0) {
      this.setState({ productsToAdd: queryParamProducts });
      // Set flag that cart processing is happening (query params)
      // This prevents checkout page from redirecting while cart is being built
      sessionStorage.setItem("cart_processing", "true");
    }
  }

  componentDidUpdate() { }

  componentWillUnmount() {
    window.clearInterval(this.cyberMondayInterval);
    this._isMounted = false;
  }

  handleClearProducts = () => {
    this.setState({ productsToAdd: [] });
  };

  async loadCheckHeadings() {
    if (
      process.env.GATSBY_ACTIVE_ENV === "development" &&
      process.env.GATSBY_CHECK_HEADINGS
    ) {
      const { default: checkHeadings } = await import("../utils/headings");
      checkHeadings();
    }
  }

  setOffset(height = 0) {
    if (!this._isMounted) {
      this._bufferedOffset = height;
      return;
    }

    this._bufferedOffset = null;
    this.setState({
      offset: height,
    });
  }

  updatePageData(data) {
    if (data.label) {
      currentPageData.label = data.label;
    }
  }

  render() {
    const {
      children,
      offset,
      activeCart,
      dispatchAddProductsToCart,
      isProcessing,
    } = this.props;
    const { productsToAdd } = this.state;

    const childrenWithProps = Children.map(children, (child) =>
      cloneElement(child, {
        updatePageData: this.updatePageData,
        offset,
      }),
    );

    // Strip all "/" from the beginning and end of the path string.
    const strippedPath = this.props.location.pathname.replace(/^\/+|\/+$/g, "");

    // Hiding nav on certain pages
    const pagesWithoutNav = ["checkout", "ca/checkout", "uk/checkout"];
    const hideNav = !!pagesWithoutNav.find((p) => strippedPath === p);

    // Hiding footer on certain pages
    const pagesWithoutFooter = ["checkout", "ca/checkout", "uk/checkout"];
    const hideFooter = !!pagesWithoutFooter.find((p) => strippedPath === p);

    return (
      <>
        {!hideNav && <SimpleHeader />}

        <SiteRoot topOffset={!hideNav ? offset : 0} id="main">
          {childrenWithProps}
        </SiteRoot>

        {!hideFooter && (
          <SimpleFooter />
        )}
        <div id="modal-root">
          {!hideNav && <FlyoutCart />}
          <LocaleSuggester />
        </div>
        <div id="zoom-root"></div>
        <CartController
          productsToAdd={productsToAdd}
          activeCart={activeCart}
          isProcessing={isProcessing}
          dispatchAddProductsToCart={dispatchAddProductsToCart}
          onClearProducts={this.handleClearProducts}
          fermatData={this.state.fermatData}
        />
      </>
    );
  }
}

const mapStateToProps = (state) => {
  const { navigation } = state;
  return {
    offset: navigation.offset,
    showUSPNotice: state.navigation.showFooterUSPNotice,
    cartQuantity: cartProductSelectors.activeCartProductQuantity(state),
    validPlanIds: state.plans.allIds,
    validProductOfferIds: state.productOffers.allIds,
    activeCart: cartSelectors.activeCart(state),
    isProcessing: cartSelectors.isProcessing(state),
  };
};

const componentWithState = connect(mapStateToProps, {
  dispatchClearCartAndAddProducts: clearCartAndAddProducts,
  dispatchAddProductsToCart: addProductsToCart,
})(RitualBase);

export default componentWithState;
